
import React, { useEffect, useRef, useState, useMemo } from 'react'

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Import the CSS for styling
import { enGB } from 'date-fns/locale';
import Papa from 'papaparse';  
import { CSVLink } from 'react-csv';
import { getAllUsers, updateUser } from '../../api/user';
import { snakeToCamel } from './AdminEditRelease';
import { createPayout, getPayouts, updatePayouts } from '../../api/payouts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'
import PayoutTable from '../shared/PayoutTable'
import Pagination from '../Paginations'

const AdminPayouts = () => {
    const paymentMethods = [
        'Wire Transfer',
        'Paypal',
        'MFS',
        'ACH'
    ]
    const monthYear = getCurrentMonthYear()
    const scheduledDate = getCurrentDate()
    const payoutFilename = "payout-" + getCurrentDate().replaceAll('/', '-') + '.csv'
    const [filteredUsers, setFilteredUsers] = useState([])
    const [payoutUser, setPayoutUser] = useState('')
    const [paymentMethod, setPaymentMethod] = useState(paymentMethods[0])
    const [amount, setAmount] = useState(0)
    const [reqDate, setReqDate] = useState(new Date())
    const [payDate, setPayDate] = useState(new Date())
    const [uploadMessage, setUploadMessage] = useState('Upload From CSV')
    const [notFoundUsers, setNotFoundUsers] = useState([])
    const [errorMsg, setErrorMsg] = useState('')
    const [tab, setTab] = useState(1)
    const [currentPage, setCurrentPage] = useState(1)
    const [historyCurrentPage, setHistoryCurrentPage] = useState(1)
    const [historyItemsPerPage] = useState(10)
    const [payoutsData, setPayoutsData] = useState([])
    const [payoutHistory, setPayoutHistory] = useState([])
    const successBtn = useRef()

    const queryClient = useQueryClient()

    // Fetch Users Query
    const { 
        data: usersData = [], 
        isLoading: isLoadingUsers,
        error: usersError
    } = useQuery({
        queryKey: ['users'],
        queryFn: getAllUsers,
        select: (data) => {
            return data.map(user => snakeToCamel(user))
        }
    })
    
    // Fetch Payouts Query
    const {
        data: payouts = [],
        isLoading: isLoadingPayouts,
        error: payoutsError
    } = useQuery({
        queryKey: ['payouts'],
        queryFn: getPayouts,
        select: (data) => {
            if (!data?.payouts) return []
            return data.payouts
               
        }
    })
    useEffect(() => {
        if(payouts.length > 0) {
            setPayoutsData(payouts.filter(payout => payout.status === "PENDING" || payout.status === "Pending")
            .map(payout => snakeToCamel({
                ...payout,
                key: payout.user_profile.id,
            })))
            setPayoutHistory(payouts.filter(payout => payout.status === "APPROVED" || payout.status === "Approved" || payout.status === "DECLINED" || payout.status === "Declined"))
        }
    }, [payouts])

    // Derive CSV payouts data from payoutsData
    const csvPayouts = useMemo(() => {
        return payoutsData?.map(payout => ({
            'Idap': payout.payeeID,
            'Amount': payout.amount,
            'SubmittedAmountCurrency': payout.currency,
            'RefCode': '',
            'EWalletMessage': '',
            'BankingMessage': `${payout.payeeID} ${monthYear}`,
            'IgnoreThresholds': 'TRUE',
            'EmailMessage': '',
            'ScheduledDate': scheduledDate,
            'VisibleWhenScheduled': 'TRUE',
            'IncomeType': 'OtherIncome',
            'MaxWithholding': 'FALSE',
            'IgnoreTaxValidation': 'FALSE',
            'AdditionalWithholding': '',
            'TreatyLimitExceeded': 'FALSE',
            'UsSource': ''
        })) || []
    }, [payoutsData, monthYear, scheduledDate])

    // Derive requests from usersData
    const requests = useMemo(() => {
        return usersData.filter(user => user.setupRequest === "true") || []
    }, [usersData])

    // Use usersData directly instead of maintaining a separate users state
    const users = usersData

    // Update filter function
    const filterUsers = (e) => {
        setPayoutUser(e.target.value)
        if (e.target.value.length > 1) {
            setFilteredUsers(users.filter(item => 
                item.email?.toLowerCase().includes(e.target.value.toLowerCase())
            ))
        } else {
            setFilteredUsers([])
        }
    }
    const updateUserMutation = useMutation({
        mutationFn: (userData) => updateUser(userData.id, userData),
        onSuccess: () => {
            // Use the existing sentSuccessBtn for success notification
            sentSuccessBtn.current.click();
            // Invalidate and refetch the users data to update the UI
            queryClient.invalidateQueries(['users']);
        },
        onError: (error) => {
            // Use the existing error handling mechanism
            handleError(error.message || 'Payment setup request failed');
        }
    });
    

    // Update Payout Status Mutation
    const updatePayoutMutation = useMutation({
        mutationFn: updatePayouts,
        onSuccess: () => {
            queryClient.invalidateQueries(['payouts'])
            successBtn.current.click()
        },
        onError: (error) => {
            setErrorMsg(error.message)
        }
    })

    // Create Payout Mutation
    const createPayoutMutation = useMutation({
        mutationFn: createPayout,
        onSuccess: () => {
            queryClient.invalidateQueries(['payouts'])
            payoutSuccess.current.click()
            // Clear form
            setPayoutUser('')
            setAmount(0)
            setPaymentMethod(paymentMethods[0])
            setReqDate(new Date())
            setPayDate(new Date())
        },
        onError: (error) => {
            handleError(error.message)
        }
    })

    function getCurrentMonthYear() {
        
        const date = new Date();
        const options = { year: 'numeric', month: 'long' };
        return date.toLocaleDateString('en-US', options).toUpperCase();
    }

    function getCurrentDate() {
        const date = new Date();
        const month = date.getMonth() + 1; // Months are zero-indexed
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2); // Get the last two digits of the year
        
        return `${month}/${day}/${year}`;
    }

    

    

   

    const currentDate = () => {
        const day = String(new Date().getDate()).padStart(2, '0'); // Get the day and pad with leading zero if needed
        const month = String(new Date().getMonth() + 1).padStart(2, '0'); // Get the month (0-indexed) and pad with leading zero
        const year = new Date().getFullYear(); // Get the full year
      
        return `${year}/${month}/${day}`; // Construct the "dd/mm/yyyy" format
    };


    const handleDecline = async  (key, id) => {
        updatePayoutMutation.mutate({payout_id: id, status: "Declined"})
    }
    const handleApprove = async  (key, id, amount) => {
        updatePayoutMutation.mutate({payout_id: id, status: "Approved"})
    }

    const sentSuccessBtn = useRef()
    const sentInvite = (key) => {
       updateUserMutation.mutate({id: key, setup_request: "Sent"})
    }



    const payoutSuccess = useRef()

    const handleError = (msg) => {
        setErrorMsg(msg)
        setTimeout(() => {
            setErrorMsg('')
        }, 3000);
    }


    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        setUploadMessage('Please Wait...')

        if (file) {
            Papa.parse(file, {
                complete: (result) => {
                    handlePayoutsFile(result.data)
                },
                header: true, 
            });
        }
    }

    const handlePayoutsFile = (payoutsFile) => {
        const usersToUpdate = []
        const usersNotFound = []
        payoutsFile.forEach(user => {
            const dbUser = users.find(item => item.email === user.user)
            if (dbUser) {
                usersToUpdate.push({...user, id: dbUser.id, payouts: dbUser.payouts})
            } else usersNotFound.push(user.user)
        })
        if (usersNotFound.length > 0) {
            setNotFoundUsers(usersNotFound)
            setUploadMessage('Upload From CSV')
        } else {
            usersToUpdate.forEach(user => {

                const userPayouts = user.payouts || []

                function generateRandomID() {
                    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                    let randomID = '';
                    for (let i = 0; i < 5; i++) {
                        randomID += characters.charAt(Math.floor(Math.random() * characters.length));
                    }
                    return randomID;
                }
                
                const payoutID = generateRandomID();
        
                const newPayout = {
                    requestDate: user.request_date,
                    method: user.payment_method,
                    currency: 'USD',
                    amount: user.amount,
                    paymentDate: user.payment_date,
                    status: 'Approved',
                    id: payoutID
                }

                userPayouts.push(newPayout)

               
            })
            setUploadMessage('Upload From CSV')
            payoutSuccess.current.click()
        }
    }

    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0'); // Get day and pad with leading zero
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Get month (0-indexed, hence +1)
        const year = date.getFullYear(); // Get year
      
        return `${year}/${month}/${day}`; // Construct "dd/mm/yyyy" format
    };
    const addPayout = async  () => {
        const userPayout = users.find(user => user.email === payoutUser) 
        console.log(userPayout)
        if (userPayout) {
            const userBalance = userPayout.balance || 0
            const payoutAmount = parseFloat(amount)
            const newPayout = {
                requestDate: formatDate(reqDate),
                method: paymentMethod,
                currency: 'USD',
                amount: payoutAmount,
                paymentDate: formatDate(payDate),
                status: 'Approved',
                userProfileId: userPayout.userId, 
            }
            createPayoutMutation.mutate(newPayout)
        } else handleError('User not found')
    }

    // History pagination
    const indexOfLastHistoryItem = historyCurrentPage * historyItemsPerPage;
    const indexOfFirstHistoryItem = indexOfLastHistoryItem - historyItemsPerPage;
    const currentHistoryItems = payoutHistory.slice(indexOfFirstHistoryItem, indexOfLastHistoryItem);
    const totalHistoryPages = Math.ceil(payoutHistory.length / historyItemsPerPage);

    const handleHistoryPageChange = (pageNumber) => {
        setHistoryCurrentPage(pageNumber);
    };

  return (
    <div className="admin-page py-5">
        <div className='container'>
            <h1 className='dash-title'>Payouts</h1>
            <div className="row">
                <div className="col-12 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading">
                            <h2 className="dash-card-title">Payouts Requests</h2>
                            <div className="d-flex">
                                <button className="dash-card-heading-btn" data-bs-toggle="modal" data-bs-target="#addPayoutModal">
                                    <i className="fi fi-sr-plus"></i>
                                    Add Payout
                                </button>
                                <CSVLink className="dash-card-heading-btn ms-2" data={csvPayouts} filename={payoutFilename}>
                                    <i className="fi fi-rr-file-export me-2"></i>
                                    Export CSV
                                </CSVLink>
                            </div>
                        </div>
                        
                        <PayoutTable 
                            payouts={payoutsData}
                            isLoading={isLoadingPayouts || isLoadingUsers}
                            error={payoutsError || usersError}
                            emptyMessage="No pending payouts available"
                            isPending={true}
                            onApprove={handleApprove}
                            onDecline={handleDecline}
                            isAdmin={true}
                        />
                    </div>
                </div>

               

                <div className="col-12 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading">
                            <h2 className="dash-card-title">Payout Setup Invite Requests</h2>
                        </div>
                        {/* Empty State */}
                        {!isLoadingUsers && !usersError && requests.length === 0 && (
                            <div className="text-center py-5">
                                <h5>No pending setup requests</h5>
                            </div>
                        )}

                        <div className="table-responsive mt-3">
                            <table className="table dash-table">
                                <thead>
                                    <tr>
                                        <td>Name</td>
                                        <td>Email</td>
                                        <td>Action</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        requests.map((request, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="table-item">
                                                        {request.firstName} {request.lastName}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        {request.email}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item d-flex">
                                                        <button 
                                                            className="table-action-btn a-green"
                                                            onClick={() => {
                                                                sentInvite(request.userId)
                                                            }}
                                                        >
                                                            <i className="fi fi-br-check"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                 {/* Payout History Table */}
                 <div className="col-12 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading">
                            <h2 className="dash-card-title">Payout History</h2>
                        </div>
                        
                        <PayoutTable 
                            payouts={currentHistoryItems}
                            isLoading={isLoadingPayouts || isLoadingUsers}
                            error={payoutsError || usersError}
                            emptyMessage="No payout history available"
                            isPending={false}
                            isAdmin={true}
                        />
                        
                        {!isLoadingPayouts && !payoutsError && payoutHistory.length > 0 && (
                            <Pagination 
                                currentPage={historyCurrentPage} 
                                totalPages={totalHistoryPages} 
                                handlePageChange={handleHistoryPageChange} 
                            />
                        )}
                    </div>
                </div>
            </div>


        </div>
                            

        <button ref={successBtn} data-bs-target="#successModal" data-bs-toggle="modal" hidden></button>
        <div className="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button type="button" className='success-close-btn' data-bs-dismiss="modal">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Payout Updated Successfully!</h5>
                    </div>
                </div>
            </div>
        </div>

        <button ref={sentSuccessBtn} data-bs-target="#sentSuccessModal" data-bs-toggle="modal" hidden></button>
        <div className="modal fade" id="sentSuccessModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="sentSuccessModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button type="button" className='success-close-btn' data-bs-dismiss="modal">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Request Updated Successfully!</h5>
                    </div>
                </div>
            </div>
        </div>


        <div className="modal fade" id="addPayoutModal" tabindex="-1" aria-labelledby="addPayoutModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="addPayoutModalLabel">Add Payout</h1>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-12 mt-3">
                                <div className="dash-form-file py-4">
                                    <i className="fi fi-rr-cloud-upload"></i>
                                    {uploadMessage}
                                    <input type="file" onChange={handleFileUpload} />
                                </div>
                            </div>
                            {
                                notFoundUsers.length > 0 && (
                                    <div className="col-12 mt-3">
                                        {
                                            notFoundUsers.map((user, i) => (
                                                <h6 className='text-danger' key={i}>User "{user}" not found</h6>
                                            ))
                                        }
                                    </div>
                                )
                            }
                            <div className="col-12 mt-3">
                                <div className="dash-form-group">
                                    <label>User</label>
                                    <div className="dropdown dash-form-dropdown">
                                        <input 
                                            type="text" 
                                            className='dash-form-input' 
                                            placeholder='Select user' 
                                            data-bs-toggle="dropdown" 
                                            aria-expanded="false"
                                            value={payoutUser}
                                            onChange={filterUsers}
                                        />
                                        <ul className="dropdown-menu">
                                            {
                                                filteredUsers.map((item, i) => (
                                                    <li key={i} onClick={() => setPayoutUser(item.email)}>
                                                        {item.email}
                                                    </li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Amount ($)</label>
                                    <input type="text" value={amount} onChange={e => setAmount(e.target.value)} placeholder='Amount' className='dash-form-input' />
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>User</label>
                                    <div className="dropdown dash-form-dropdown">
                                        <button className="dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            {paymentMethod}
                                        </button>
                                        <ul className="dropdown-menu">
                                            {
                                                paymentMethods.map((item, i) => (
                                                    <li key={i} onClick={() => setPaymentMethod(item)}>
                                                        {item}
                                                    </li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Request Date</label>
                                    <DatePicker
                                        id="date-picker"
                                        selected={reqDate}
                                        onChange={e => setReqDate(e)}
                                        dateFormat="yyyy/MM/dd"
                                        locale={enGB} 
                                        className='dash-form-input'
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Payment Date</label>
                                    <DatePicker
                                        id="date-picker"
                                        selected={payDate}
                                        onChange={e => setPayDate(e)}
                                        dateFormat="yyyy/MM/dd"
                                        locale={enGB} 
                                        className='dash-form-input'
                                    />
                                </div>
                            </div>
                            {
                                errorMsg !== '' && (
                                    <div className="col-12 mt-3">
                                        <h6 className='text-danger text-center'>{errorMsg}</h6>
                                    </div>
                                )
                            }
                            <div className="col-12 mt-3">
                                <div className="dash-form-btns">
                                    <button 
                                        className='dash-form-btn' 
                                        onClick={addPayout}
                                    >Submit</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <button ref={payoutSuccess} data-bs-target="#payoutSuccessModal" data-bs-toggle="modal" hidden></button>
        <div className="modal fade" id="payoutSuccessModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="payoutSuccessModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button type="button" className='success-close-btn' data-bs-dismiss="modal">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Payouts Added Successfully!</h5>
                    </div>
                </div>
            </div>
        </div>

    </div>
  )
}

export default AdminPayouts